import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { App as CapacitorApp, URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';

import {
	fetchSubscriptions,
	fetchTickets,
	resyncPassengerBookings,
	useBusinessLayer,
	useUser,
} from '@mopla/business-logic';

/**
 * Persists the last verification oobCode that has already been handled.
 * Capacitor keeps replaying the original launch URL for the whole native process
 * lifetime — via getLaunchUrl() and by re-delivering it to a freshly registered
 * appUrlOpen listener — even across webview reloads (e.g. the reload triggered on
 * logout). Without this guard the one-time verification deep link gets replayed
 * after logout and its already-consumed oobCode fails, landing the user on the
 * "something went wrong, please log in" screen instead of the welcome screen.
 */
const HANDLED_VERIFY_OOBCODE_KEY = 'handledVerifyOobCode';

export function AppListener() {
	const navigate = useNavigate();

	const location = useLocation();
	const businessLayer = useBusinessLayer();
	const [platform] = useState(Capacitor.getPlatform());
	const historyStack = useRef<string[]>([]);
	const { userData } = useUser();

	useEffect(() => {
		historyStack.current.push(location.pathname + location.search);
	}, [location]);

	useEffect(() => {
		let listener: PluginListenerHandle | null = null;

		const handleDeepLinkUrl = (rawUrl: string) => {
			const url = new URL(rawUrl);
			const isPaymentRedirect = url.searchParams.has('isPaymentRedirect');
			const isTrueLayerRedirect = url.searchParams.has('truelayer_redirect');

			/**
			 * One-time verification links (oobCode) must only be processed once.
			 * Capacitor keeps returning the original launch URL — both via
			 * getLaunchUrl() and by re-delivering it to a freshly registered
			 * appUrlOpen listener — even after a webview reload (e.g. the reload
			 * performed on logout). Without this guard the already-consumed
			 * verification code gets replayed after logout, its applyActionCode
			 * fails, and the user lands on the "something went wrong, please log in"
			 * screen instead of the welcome screen. Handle each verification URL once.
			 */
			const oobCode = url.searchParams.get('oobCode');
			if (oobCode) {
				if (
					window.localStorage.getItem(HANDLED_VERIFY_OOBCODE_KEY) === oobCode
				) {
					return;
				}
				window.localStorage.setItem(HANDLED_VERIFY_OOBCODE_KEY, oobCode);
			}

			businessLayer.dispatch(resyncPassengerBookings());

			if (!isPaymentRedirect && !isTrueLayerRedirect && url.pathname) {
				/** If doesn't relate to the payment flow, navigate to the requested page */
				navigate(url.pathname + url.search);
			}
			/** If no match, do nothing - let regular routing logic take over */
		};

		const setupListener = async () => {
			listener = await CapacitorApp.addListener(
				'appUrlOpen',
				(event: URLOpenListenerEvent) => {
					handleDeepLinkUrl(event.url);
				}
			);

			/** Handle cold start: getLaunchUrl retrieves the URL that opened the app when it was not running */
			const launchUrl = await CapacitorApp.getLaunchUrl();
			if (launchUrl?.url) {
				handleDeepLinkUrl(launchUrl.url);
			}
		};

		setupListener();

		return () => {
			if (listener) {
				listener.remove();
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		let listener: PluginListenerHandle | null = null;

		const setupListener = async () => {
			listener = await CapacitorApp.addListener('resume', () => {
				if (userData) {
					businessLayer.dispatch(fetchSubscriptions());
					businessLayer.dispatch(fetchTickets());
					businessLayer.dispatch(resyncPassengerBookings());
				}
			});
		};

		setupListener();

		return () => {
			if (listener) {
				listener.remove();
			}
		};
	}, [businessLayer, userData]);

	/** TODO MC-10248 This doesn't work without adding Network plugin, which requires a new native build */
	// useEffect(() => {
	// 	let listener: PluginListenerHandle | null = null;
	//
	// 	const setupListener = async () => {
	// 		listener = await Network.addListener('networkStatusChange', (status) => {
	// 			networkStatus$.next(status.connected);
	// 		});
	// 	};
	//
	// 	setupListener();
	//
	// 	return () => {
	// 		if (listener) {
	// 			listener.remove();
	// 		}
	// 	};
	// }, []);

	useEffect(() => {
		const listeners: PluginListenerHandle[] = [];

		const setupListeners = async () => {
			if (platform !== 'web') {
				document.body.classList.add(`platform-${platform}`);

				const l1 = await Keyboard.addListener('keyboardWillHide', () => {
					document.body.style.removeProperty('--keyboard-height');
					document.body.classList.remove('keyboard-is-open');
				});
				const l2 = await Keyboard.addListener('keyboardWillShow', (info) => {
					document.body.style.setProperty(
						'--keyboard-height',
						info.keyboardHeight + 'px'
					);
					document.body.classList.add('keyboard-is-open');
				});
				const l3 = await Keyboard.addListener('keyboardDidShow', () => {
					document.activeElement?.scrollIntoView({ behavior: 'smooth' });
				});
				listeners.push(l1, l2, l3);
			}
		};

		setupListeners();

		return () => {
			listeners.forEach((l) => l.remove());
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (platform === 'android') {
			let backButtonListener: PluginListenerHandle | null = null;

			const setupListener = async () => {
				backButtonListener = await CapacitorApp.addListener(
					'backButton',
					({ canGoBack }): void => {
						const currentPath = location.pathname + location.search;
						const prevPath =
							historyStack.current[historyStack.current.length - 2] || '';

						const isOnHome = currentPath.startsWith('/home');

						if (isOnHome && prevPath.startsWith('/welcome')) {
							CapacitorApp.exitApp();
							return;
						}

						if (canGoBack) {
							window.history.back();
						} else {
							CapacitorApp.exitApp();
						}
					}
				);
			};

			setupListener();

			return () => {
				if (backButtonListener) {
					backButtonListener.remove();
				}
			};
		} else {
			return;
		}
	}, [platform, location]);

	return null;
}
